import axios, { AxiosResponse } from 'axios';
import xorBy from 'lodash/xorBy';
import { CandidateUpdateProfileJob } from '~/utils/candidate';
import hasLangChanged from '~/utils/languages/hasLangChanged';
import AuthService from './AuthService';
import { BaseService } from './BaseService';
import {
    Candidate,
    CandidateResponse,
    CareerField,
    Employer,
    Language,
    PredefinedSkill,
    ProfileVisibility,
    Sector,
    transformCandidate,
    transformPredefinedSkills,
    transformWorkAuthorization,
    WorkAuthorization,
    WorkAuthorizationResponse,
} from './types/candidate';
import {
    ProfileImportResult,
    SectionType,
    transformProfileImportResultFromServer,
} from './types/import';

export * from './types/candidate';

export default class CandidateService extends BaseService {
    constructor() {
        super('candidate');
    }

    async getMe() {
        return axios({
            url: this.getUrl('me', {
                include: [
                    'summary',
                    'skills',
                    'preferences',
                    'work_experiences',
                    'links',
                    'memberships',
                    'nationalities',
                    'emailsettings',
                ],
            }),
        }).then((res: AxiosResponse<CandidateResponse>) => transformCandidate(res.data));
    }

    async createSchool(name: string) {
        return axios({
            url: this.getUrl('school'),
            method: 'POST',
            data: { name },
        }).then((res: AxiosResponse<{ id: number }>) => res.data);
    }

    /* WorkAuthorizations */

    async getWorkAuthorizations() {
        return axios({
            url: this.getUrl('workauthorization'),
        }).then((res: AxiosResponse<WorkAuthorizationResponse[]>) =>
            res.data.map(transformWorkAuthorization)
        );
    }

    async createWorkAuthorization(workAuthorization: WorkAuthorization) {
        return axios({
            url: this.getUrl('workauthorization'),
            method: 'POST',
            data: workAuthorization,
        }).then(this.isOK);
    }
    async deleteWorkAuthorization(id: string) {
        return axios({
            url: this.getUrl(`workauthorization/${id}`),
            method: 'DELETE',
        }).then(this.isOK);
    }

    async saveWorkAuthorizations(
        workAuthorizations: WorkAuthorization[],
        oldWordAuthorizations: WorkAuthorization[]
    ) {
        const errors: string[] = [];

        const wa = workAuthorizations.filter(wa => !wa.implicit);
        const waOld = oldWordAuthorizations.filter(wa => !wa.implicit);

        const waAdd = wa.reduce<WorkAuthorization[]>(
            (acc, cur) =>
                cur.implicit || waOld.some(wa => wa.code === cur.code && wa.type === cur.type)
                    ? acc
                    : [...acc, cur],
            []
        );
        const waDelete = xorBy(
            wa.filter(w => !!w.id),
            waOld,
            'id'
        );

        for await (const wa of waAdd)
            this.createWorkAuthorization(wa).catch(err => errors.push(err));

        for await (const wa of waDelete)
            this.deleteWorkAuthorization(wa.id).catch(err => errors.push(err));

        return !errors.length ? Promise.resolve(true) : Promise.reject(errors);
    }

    /* Summary */

    async saveSummary(summary: string) {
        return axios({
            url: this.getUrl('me'),
            method: 'PUT',
            data: { summary },
        }).then(this.isOK);
    }

    async saveMe(data: Partial<Candidate>) {
        return axios({
            url: this.getUrl('me'),
            method: 'PUT',
            data: data,
        }).then(this.isOK);
    }

    /* Languages */

    async createLanguage(language: Language) {
        return axios({
            url: this.getUrl('language'),
            method: 'POST',
            data: {
                languageId: language.languageId,
                proficiencyId: language.proficiencyId,
            },
        }).then(this.isOK);
    }
    async updateLanguage(language: Language) {
        return axios({
            url: this.getUrl(`language/${language.id}`),
            method: 'PUT',
            data: {
                languageId: language.languageId,
                proficiencyId: language.proficiencyId,
            },
        }).then(this.isOK);
    }
    async deleteLanguage(id: string) {
        return axios({
            url: this.getUrl(`language/${id}`),
            method: 'DELETE',
        }).then(this.isOK);
    }

    async setShowWarning(id: string, flag: boolean) {
        return axios({
            url: this.getUrl(`csp/${id}/showwarning/${flag}`),
            method: 'PATCH',
        }).then(this.isOK);
    }

    async saveLanguages(languages: Language[], oldLanguages: Language[]) {
        const errors: string[] = [];

        for await (const lang of languages) {
            const hasChanged = hasLangChanged(lang, oldLanguages);

            if (lang.id && hasChanged)
                await this.updateLanguage(lang).catch(err => errors.push(err));
            else if (!lang.id && lang.languageId && lang.proficiencyId)
                await this.createLanguage(lang).catch(err => errors.push(err));
        }

        const langDeleted = xorBy(
            languages.filter(l => !!l.id),
            oldLanguages,
            'id'
        );
        for await (const lang of langDeleted) await this.deleteLanguage(lang.id);

        return !errors.length ? Promise.resolve(true) : Promise.reject(errors);
    }

    /* Skills */
    async getAllSkills() {
        return axios({
            url: this.getUrl('skills'),
        }).then((res: AxiosResponse<{ results: PredefinedSkill[] }>) =>
            transformPredefinedSkills(res.data)
        );
    }

    /* Sectors */
    async getAllSectors() {
        return axios({
            url: this.getUrl('sectors'),
        }).then((res: AxiosResponse<{ results: Sector[] }>) => res.data.results);
    }

    /* Career Fields */
    async getAllCareerFields() {
        return axios({
            url: this.getUrl('careerfields'),
        }).then((res: AxiosResponse<{ results: CareerField[] }>) => res.data.results);
    }

    /* Employers */
    async getAllEmployers(includeCustomByCandidateId?: string) {
        const params: { includeCustomFor?: string } = {};
        if (includeCustomByCandidateId) {
            params.includeCustomFor = includeCustomByCandidateId;
        }

        return axios({
            url: this.getUrl('employers'),
            params,
        }).then((res: AxiosResponse<{ results: Employer[] }>) => res.data.results);
    }

    /* Profile */

    async updateCandidateProfile(
        request: CandidateUpdateProfileJob,
        dispatchProfileUpdated = true
    ) {
        return axios({
            url: this.getUrl('profile'),
            method: 'POST',
            data: request,
        }).then(res => {
            if (dispatchProfileUpdated) {
                this.dispatchProfileUpdated(request);
            }

            return this.isOK(res);
        });
    }

    async setProfileVisibility(visibility: ProfileVisibility) {
        return axios({
            url: this.getUrl('profile'),
            method: 'POST',
            data: {
                userId: await AuthService.getUserId(),
                candidate: {
                    op: 'update',
                    value: {
                        profileVisibility: visibility,
                    },
                },
            } as CandidateUpdateProfileJob,
        }).then(this.isOK);
    }

    async getLastProfileImport(): Promise<ProfileImportResult | null> {
        return axios({
            url: this.getUrl('import/latest'),
            method: 'GET',
        }).then((res: AxiosResponse<ProfileImportResult | null>) =>
            transformProfileImportResultFromServer(res.data)
        );
    }

    async updateLastProfileImportReviewed(id: string, value: boolean) {
        return axios({
            url: this.getUrl(`import/${id}/update-reviewed`),
            method: 'PATCH',
            data: {
                isReviewed: value,
            },
        }).then(this.isOK);
    }

    async updateProfileImportSectionsResolved(
        importId: string,
        sectionTypes: SectionType[],
        resolved: boolean
    ) {
        return axios({
            url: this.getUrl(`import/${importId}/update-resolved`),
            method: 'PATCH',
            data: { sectionTypes: sectionTypes, areResolved: resolved },
        }).then(this.isOK);
    }

    async importProfile(
        profileLink: string,
        sectionTypes: SectionType[]
    ): Promise<ProfileImportResult | null> {
        return await axios({
            url: this.getUrl(`import/linkedin-proxycurl`),
            method: 'POST',
            data: {
                sectionTypes: sectionTypes,
                linkedinProfileLink: profileLink,
            },
        }).then((res: AxiosResponse<ProfileImportResult | null>) =>
            transformProfileImportResultFromServer(res.data)
        );
    }

    async importCV(file: any, sectionTypes: SectionType[]): Promise<ProfileImportResult | null> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('sectionTypes', JSON.stringify(sectionTypes));

        return await axios({
            url: this.getUrl(`import/resume-file`),
            method: 'POST',
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
        }).then((res: AxiosResponse<ProfileImportResult | null>) =>
            transformProfileImportResultFromServer(res.data)
        );
    }

    /**
     * Dispatches a custom event 'candidate:profile:update' to notify listeners about
     * a candidate profile update.
     * @param request - The CandidateUpdateProfileJob request containing update details.
     */
    dispatchProfileUpdated(request: CandidateUpdateProfileJob) {
        const event = new CustomEvent('candidate:profile:update', {
            detail: request,
        });
        window.dispatchEvent(event);
    }
}
